/**
 * `/accounts/:id` — per-customer detail (OCP-167).
 *
 * Header (email + status pill + signup date + suspend/unsuspend +
 * force-logout buttons) plus four tabs: Users / Subscriptions /
 * Mail usage / Licenses. Destructive actions sit behind a confirm
 * modal that requires typing the customer email (anti-fat-finger).
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api, ApiError, readErrorKey } from '../lib/api';
import { OperatorMailForm } from '../components/OperatorMailForm';
import { AffiliateCard } from '../components/AffiliateCard';
import { TermsAcceptanceCard } from '../components/TermsAcceptanceCard';
import { LoginActivityCard } from '../components/LoginActivityCard';
import { RedeemAttemptsCard } from '../components/RedeemAttemptsCard';
import { AccountAiKeysCard } from '../components/AccountAiKeysCard';
import { AiOverageCard } from '../components/AiOverageCard';
import { fmtDateTime, fmtDate } from '../lib/format-time';
import { CompanyPackagesPanel } from '../components/CompanyPackagesPanel';

type AccountStatus = 'active' | 'suspended' | 'pending_email_verify';

interface UserRow {
  id: string;
  email: string;
  email_verified: boolean;
  role: string;
  totp_enabled: boolean;
  /** OCP-656 - the dashboard language the user chose (de | en), null = browser default. */
  locale?: string | null;
  last_login_at: string | null;
  created_at: string;
}

interface SubscriptionRow {
  id: string;
  product_id: string;
  product_display_name: string;
  status: string;
  /** Stored column - defaults to 'paddle' even on rows no provider ever issued. */
  provider: string;
  paddle_subscription_id: string | null;
  polar_subscription_id: string | null;
  /** OCP-531 - resolved from the link columns; null = no provider link. */
  billing_provider: 'paddle' | 'polar' | null;
  /** OCP-777 - provider-renewed, a rolled free plan, or a finite grant that ends at current_period_end. */
  renewal?: 'provider' | 'perpetual_free' | 'ends';
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;
  created_at: string;
}

interface LicenseRow {
  id: string;
  display_name: string;
  tier: string;
  status: string;
  max_instances: number | null;
  license_key_prefix: string;
  issued_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  instance_count: number;
}

interface MailUsage {
  sends_used: number | null;
  sends_total: number | null;
  period_end: string | null;
  source: 'oms' | 'unavailable';
}

// OCP-474 - a provisioned workspace (tenant) owned by this account.
// `workspace_url` is resolved server-side on the tenant zone (orbo.to),
// never orboto.io (OCP-458).
interface TenantRow {
  id: string;
  subdomain: string;
  custom_domain: string | null;
  custom_domain_status: string | null;
  workspace_url: string;
  status: string;
  is_demo: boolean;
  region: string;
  pool_tag: string;
  vps_server_id: string | null;
  worker_name: string | null;
  storage_quota_gb: number;
  origin: string;
  image_tag: string | null;
  license_expires_at: string | null;
  created_at: string;
  activated_at: string | null;
}

// OCP-475 - the waitlist entry an invite code was minted from, when the
// code carries one. Answers "did this customer come off the waitlist?".
interface OriginWaitlist {
  id: string;
  email: string;
  company: string | null;
  status: string;
  team_size_bucket: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  invited_at: string | null;
  invite_mail_sent_at: string | null;
  signed_up_at: string | null;
  created_at: string;
}

// OCP-475 - one invite/prepaid code this account originated from. `code`
// is the human code (show-keys-not-uuids). `redeemed_at`/`redeemer_email`
// are null on the tenant-origin fallback path.
interface OriginCode {
  redemption_id: string | null;
  code_id: string;
  code: string;
  kind: string;
  tier: string;
  category: string;
  duration_months: number | null;
  seats: number | null;
  created_by: string;
  note: string | null;
  redeemed_at: string | null;
  redeemer_email: string | null;
  tenant_id: string | null;
  source: 'ledger' | 'tenant_origin';
  waitlist: OriginWaitlist | null;
}

interface AccountOrigin {
  codes: OriginCode[];
}

// ---- OCP-524 responsible contact ------------------------------------

interface ResponsibleContact {
  name: string;
  email: string;
  phone: string | null;
  position: string | null;
  set_at: string;
  set_by: string;
  source: 'portal' | 'operator';
}

interface ResponsibleContactChange {
  id: string;
  changed_at: string;
  changed_by: string;
  source: 'portal' | 'operator';
  previous_name: string | null;
  previous_email: string | null;
  previous_phone: string | null;
  previous_position: string | null;
  new_name: string;
  new_email: string;
  new_phone: string | null;
  new_position: string | null;
  confirmation_text: string | null;
  notice_mail_id: string | null;
  notice_recipients: Array<{
    role: 'new_contact' | 'previous_contact' | 'account';
    email: string;
    message_id: string | null;
    error: string | null;
  }> | null;
  notice_mail_error: string | null;
  notice_reference: string | null;
}

interface AccountDetail {
  id: string;
  email: string;
  display_name: string | null;
  company_name: string | null;
  vat_id: string | null;
  default_currency: string;
  signup_referrer: string | null;
  signup_landing_path: string | null;
  signup_ref: string | null;
  mail_credit_balance: number;
  mail_overage_mode: string;
  mail_monthly_overage_cap_eur_cents: number;
  wallet_balance_cents: number;
  wallet_balance_microcents: number;
  status: AccountStatus;
  created_at: string;
  archived_at: string | null;
  // OCP-515 - carried-but-unconsumed redeem code (stuck-mid-claim signal).
  pending_redeem_code: string | null;
  pending_redeem_code_set_at: string | null;
  // OCP-524 - the authorized responsible contact person required by the Terms,
  // plus the append-only text-form evidence trail of every change to it.
  responsible_contact: ResponsibleContact | null;
  responsible_contact_history: ResponsibleContactChange[];
  /** OCP-534 - the Sec. 14 BGB declaration + the operator refusal state. */
  business_capacity: BusinessCapacityAdminRow | null;
  // OCP-526 - the order record: what this customer ordered, when, at which
  // price snapshot, and whether the order confirmation reached them.
  orders: OrderRow[];
  users: UserRow[];
  subscriptions: SubscriptionRow[];
  // OCP-532 - the mirrored invoices of whichever Merchant of Record issued
  // them, plus the summary that explains an empty list.
  invoices: InvoiceRow[];
  billing: BillingSummary;
  licenses: LicenseRow[];
  mail_usage: MailUsage;
  tenants: TenantRow[];
  tenant_zone: string;
  origin: AccountOrigin;
}

// ---- OCP-532 invoices + billing summary -----------------------------

interface InvoiceRow {
  id: string;
  provider: 'paddle' | 'polar';
  provider_invoice_id: string;
  provider_invoice_number: string | null;
  subscription_id: string | null;
  total_eur_cents: number;
  currency: string;
  status: string;
  issued_at: string;
  paid_at: string | null;
}

interface BillingSummary {
  paddle_customer_id: string | null;
  polar_customer_id: string | null;
  providers: Array<'paddle' | 'polar'>;
  invoice_count: number;
  empty_invoice_reason: 'no_billing_provider' | 'awaiting_first_invoice' | null;
}

type Tab = 'users' | 'subscriptions' | 'invoices' | 'mail' | 'wallet' | 'licenses';

export function AdminAccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AccountDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<Tab>('users');
  const [confirmAction, setConfirmAction] = useState<
    | { kind: 'suspend' | 'unsuspend' | 'force-logout' }
    | { kind: 'reset-password'; userId: string; userEmail: string }
    | null
  >(null);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    setLoading(true);
    try {
      const res = await api<AccountDetail>(`/v1/admin/accounts/${id}`);
      setData(res);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setError('Account not found.');
        } else {
          setError(`Load failed (HTTP ${err.status}).`);
        }
      } else {
        setError('Load failed.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) return <div className="text-sm text-gray-500">Loading…</div>;
  if (error) {
    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => navigate('/accounts')}
          className="text-xs text-brand-500 hover:underline"
        >
          ← Back to accounts
        </button>
        <div className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4">
      <Link to="/accounts" className="text-xs text-brand-500 hover:underline">
        ← Back to accounts
      </Link>

      <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-mono text-sm">{data.email}</div>
            {data.display_name && (
              <div className="text-sm text-gray-700 dark:text-gray-300">{data.display_name}</div>
            )}
            {data.company_name && (
              <div className="text-xs text-gray-500">{data.company_name}</div>
            )}
            <div className="mt-1 flex items-center gap-2">
              <StatusPill status={data.status} />
              <span className="text-xs text-gray-500">
                Joined {fmtDate(data.created_at)}
              </span>
              {data.signup_referrer && (
                <span className="text-xs text-gray-500">
                  via {data.signup_referrer}
                  {data.signup_ref && ` (?ref=${data.signup_ref})`}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {/* OCP-649 - one-off operator mail to the account email. */}
            {!data.archived_at && <OperatorMailForm target={{ account_id: data.id }} />}
            {data.status === 'suspended' ? (
              <button
                type="button"
                onClick={() => setConfirmAction({ kind: 'unsuspend' })}
                className="rounded border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-1.5 text-xs text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100"
              >
                Unsuspend
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmAction({ kind: 'suspend' })}
                className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-1.5 text-xs text-red-700 dark:text-red-300 hover:bg-red-100"
              >
                Suspend account
              </button>
            )}
            <button
              type="button"
              onClick={() => setConfirmAction({ kind: 'force-logout' })}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
            >
              Force-logout
            </button>
          </div>
        </div>
      </div>

      <InstanceCard tenants={data.tenants} />

      <PendingClaimBanner
        code={data.pending_redeem_code}
        setAt={data.pending_redeem_code_set_at}
      />

      <ResponsibleContactCard
        customerId={data.id}
        contact={data.responsible_contact}
        history={data.responsible_contact_history}
        onRecorded={load}
      />

      {/* OCP-534 - the AGB V4 Präambel B declaration + the operator refusal. Sits
          next to the responsible contact because they are the two Terms
          obligations that gate a NEW contract conclusion. */}
      <BusinessCapacityCard
        customerId={data.id}
        capacity={data.business_capacity}
        onChanged={load}
      />

      {/* OCP-525 - the third contract-conclusion gate: which Terms version
          this account accepted, and the 13.3 notice/decision state. */}
      <TermsAcceptanceCard customerId={data.id} />

      <OrdersCard customerId={data.id} orders={data.orders} onChanged={load} />

      {/* OCP-1036 - a price agreed with this account; its checkout charges it. */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
        <h2 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Company package</h2>
        <CompanyPackagesPanel customerId={data.id} compact />
      </div>

      <OriginCard origin={data.origin} />

      <AffiliateCard customerId={data.id} />

      <LoginActivityCard customerId={data.id} />

      <RedeemAttemptsCard customerId={data.id} />

      <AccountAiKeysCard customerId={data.id} />

      {/* OCP-583 - AI spend posture read + operator overage write. */}
      <AiOverageCard customerId={data.id} />

      <div className="flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-800">
        {(['users', 'subscriptions', 'invoices', 'mail', 'wallet', 'licenses'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`shrink-0 whitespace-nowrap px-3 py-1.5 text-sm capitalize ${
              tab === t
                ? 'border-b-2 border-brand-500 font-medium text-brand-700'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            {t === 'mail'
              ? 'Mail usage'
              : t === 'subscriptions'
                ? 'Subscriptions'
                : t === 'invoices'
                  ? `Invoices (${data.invoices.length})`
                  : t === 'wallet'
                    ? 'Wallet'
                    : t === 'users'
                      ? `Users (${data.users.length})`
                      : `Licenses (${data.licenses.length})`}
          </button>
        ))}
      </div>

      {tab === 'users' && (
        <UsersTab
          users={data.users}
          onTriggerReset={(u) =>
            setConfirmAction({ kind: 'reset-password', userId: u.id, userEmail: u.email })
          }
        />
      )}
      {tab === 'subscriptions' && <SubscriptionsTab subs={data.subscriptions} />}
      {tab === 'invoices' && (
        <InvoicesTab invoices={data.invoices} billing={data.billing} />
      )}
      {tab === 'mail' && <MailTab mailUsage={data.mail_usage} customer={data} />}
      {tab === 'wallet' && (
        <WalletTab
          customerId={data.id}
          initialBalanceMicrocents={data.wallet_balance_microcents}
        />
      )}
      {tab === 'licenses' && <LicensesTab licenses={data.licenses} />}

      {confirmAction && (
        <ConfirmModal
          action={confirmAction}
          customer={data}
          onCancel={() => setConfirmAction(null)}
          onDone={async () => {
            setConfirmAction(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function UsersTab({
  users,
  onTriggerReset,
}: {
  users: UserRow[];
  onTriggerReset: (u: UserRow) => void;
}) {
  if (users.length === 0) {
    return <div className="text-sm text-gray-500">No users yet.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm">
      <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
        <tr>
          <th className="px-3 py-2">Email</th>
          <th className="px-3 py-2">Verified</th>
          <th className="px-3 py-2">Role</th>
          <th className="px-3 py-2">TOTP</th>
          <th className="px-3 py-2" title="Dashboard language the user chose (OCP-656); blank = browser default">Lang</th>
          <th className="px-3 py-2">Last login</th>
          <th className="px-3 py-2 text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
        {users.map((u) => (
          <tr key={u.id}>
            <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
            <td className="px-3 py-2 text-xs">{u.email_verified ? '✓' : '—'}</td>
            <td className="px-3 py-2 text-xs">{u.role}</td>
            <td className="px-3 py-2 text-xs">{u.totp_enabled ? '✓' : '—'}</td>
            <td className="px-3 py-2 text-xs uppercase" data-testid={`account-user-locale-${u.id}`}>{u.locale ?? '—'}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
              {u.last_login_at ? fmtDate(u.last_login_at) : '—'}
            </td>
            <td className="px-3 py-2 text-right">
              <button
                type="button"
                onClick={() => onTriggerReset(u)}
                className="text-xs text-brand-500 hover:underline"
              >
                Send password reset
              </button>
            </td>
          </tr>
        ))}
      </tbody>
      </table>
    </div>
  );
}

/**
 * OCP-531 - the billing link an operator has to be able to read at a glance.
 *
 * Shows the RESOLVED provider (from the id columns) plus the id that
 * cancel/reactivate would actually be called with. A row with no link renders
 * "none" and states the consequence, because that is exactly the case where
 * the customer has no cancel button and the API answers 409
 * `no_billing_provider_link` - previously invisible to the operator, who saw
 * the stored `provider` column claiming "paddle" on a row Paddle never issued.
 */
function BillingLinkCell({ sub }: { sub: SubscriptionRow }) {
  const linkId =
    sub.billing_provider === 'polar'
      ? sub.polar_subscription_id
      : sub.billing_provider === 'paddle'
        ? sub.paddle_subscription_id
        : null;
  if (!sub.billing_provider) {
    return (
      <div data-testid="sub-billing-link">
        <span className="rounded bg-gray-200 px-1.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
          none
        </span>
        <div className="mt-0.5 text-xs text-gray-500">
          No provider link - cannot be cancelled from the portal
        </div>
        {/* The stored column claims a provider on rows nobody issued (its
            NOT NULL default). Show it as raw data so the operator is not
            surprised by it elsewhere, never as the answer. */}
        <div className="mt-0.5 text-xs text-gray-500">
          stored provider column: {sub.provider}
        </div>
      </div>
    );
  }
  const migrated =
    Boolean(sub.paddle_subscription_id) && Boolean(sub.polar_subscription_id);
  return (
    <div data-testid="sub-billing-link">
      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
        {sub.billing_provider}
      </span>
      <div className="mt-0.5 font-mono text-xs text-gray-500">{linkId}</div>
      {migrated && (
        <div className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
          Both links present - lifecycle actions use {sub.billing_provider}
        </div>
      )}
      {sub.provider !== sub.billing_provider && (
        <div className="mt-0.5 text-xs text-gray-500">
          stored provider column: {sub.provider}
        </div>
      )}
    </div>
  );
}

function SubscriptionsTab({ subs }: { subs: SubscriptionRow[] }) {
  if (subs.length === 0) {
    return <div className="text-sm text-gray-500">No subscriptions.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm">
      <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
        <tr>
          <th className="px-3 py-2">Product</th>
          <th className="px-3 py-2">Status</th>
          <th className="px-3 py-2">Billed through</th>
          <th className="px-3 py-2">Period end</th>
          <th className="px-3 py-2">Cancel-at-end</th>
          <th className="px-3 py-2">Created</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
        {subs.map((s) => (
          <tr key={s.id}>
            <td className="px-3 py-2">
              <div>{s.product_display_name}</div>
              <div className="font-mono text-xs text-gray-500">{s.product_id}</div>
            </td>
            <td className="px-3 py-2 text-xs">{s.status}</td>
            <td className="px-3 py-2 text-xs">
              <BillingLinkCell sub={s} />
            </td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
              {s.current_period_end
                ? fmtDate(s.current_period_end)
                : '—'}
              {s.renewal === 'ends' && (
                <span data-testid={`sub-renewal-${s.id}`} className="ml-1 rounded bg-gray-100 dark:bg-gray-800 px-1 text-[10px] uppercase text-gray-500">
                  fixed term, ends
                </span>
              )}
              {s.renewal === 'perpetual_free' && (
                <span data-testid={`sub-renewal-${s.id}`} className="ml-1 rounded bg-gray-100 dark:bg-gray-800 px-1 text-[10px] uppercase text-gray-500">
                  free, rolls
                </span>
              )}
            </td>
            <td className="px-3 py-2 text-xs">{s.cancel_at_period_end ? '✓' : '—'}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
              {fmtDate(s.created_at)}
            </td>
          </tr>
        ))}
      </tbody>
      </table>
    </div>
  );
}

/**
 * OCP-532 - the operator's invoice view, and the reason an empty one is empty.
 *
 * "The customer says their invoice list is empty" used to be unanswerable from
 * any surface we own: there was no invoice section here at all, and the real
 * cause (the table could not hold a Polar invoice, so the Polar webhook never
 * wrote one) was invisible. The summary strip names the providers that actually
 * bill the account - resolved from the subscription LINK columns, not from
 * `portal_subscriptions.provider` - and states which of the two empty-list
 * reasons applies.
 */
function InvoicesTab({
  invoices,
  billing,
}: {
  invoices: InvoiceRow[];
  billing: BillingSummary;
}) {
  const emptyReason =
    billing.empty_invoice_reason === 'no_billing_provider'
      ? 'No provider bills this account (free plan, code redemption or nothing purchased yet), so there is nothing to invoice. This is expected, not a fault.'
      : billing.empty_invoice_reason === 'awaiting_first_invoice'
        ? `This account is billed through ${billing.providers.join(' + ')}, but no invoice has been mirrored yet. Either the first billing period has not closed, or the provider webhook did not reach us - check the audit log for portal_invoice.mirror_skipped_no_customer.`
        : null;

  return (
    <div className="space-y-4">
      <div
        data-testid="account-billing-summary"
        className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 text-sm"
      >
        <div className="text-xs uppercase text-gray-500">Billed through</div>
        <div className="mt-0.5">
          {billing.providers.length > 0 ? (
            billing.providers.join(' + ')
          ) : (
            <span className="text-gray-500">no billing provider</span>
          )}
        </div>
        <div className="mt-2 grid gap-1 text-xs text-gray-600 dark:text-gray-400 sm:grid-cols-2">
          <div>
            Paddle customer:{' '}
            <code className="font-mono">{billing.paddle_customer_id ?? '-'}</code>
          </div>
          <div>
            Polar customer:{' '}
            <code className="font-mono">{billing.polar_customer_id ?? '-'}</code>
          </div>
        </div>
      </div>

      {emptyReason && (
        <div
          data-testid="account-invoices-empty-reason"
          className="rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
        >
          {emptyReason}
        </div>
      )}

      {invoices.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Issued</th>
                <th className="px-3 py-2">Provider</th>
                <th className="px-3 py-2">Invoice</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Paid</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {invoices.map((i) => (
                <tr key={i.id} data-testid={`account-invoice-${i.id}`}>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    {fmtDate(i.issued_at)}
                  </td>
                  <td className="px-3 py-2 text-xs">{i.provider}</td>
                  <td className="px-3 py-2">
                    <div>{i.provider_invoice_number ?? '-'}</div>
                    <div className="font-mono text-xs text-gray-500">
                      {i.provider_invoice_id}
                    </div>
                  </td>
                  <td className="px-3 py-2 tabular-nums text-xs">
                    {(i.total_eur_cents / 100).toFixed(2)} {i.currency}
                  </td>
                  <td className="px-3 py-2 text-xs">{i.status}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    {i.paid_at ? fmtDate(i.paid_at) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MailTab({
  mailUsage,
  customer,
}: {
  mailUsage: MailUsage;
  customer: AccountDetail;
}) {
  const [resyncBusy, setResyncBusy] = useState(false);
  const [resyncResult, setResyncResult] = useState<
    { ok: boolean; reason: string } | null
  >(null);

  async function handleResync() {
    setResyncBusy(true);
    setResyncResult(null);
    try {
      const r = await api<{ ok: boolean; reason: string }>(
        `/v1/admin/accounts/${customer.id}/mail-quota/resync`,
        { method: 'POST' },
      );
      setResyncResult(r);
    } catch (err) {
      setResyncResult({ ok: false, reason: err instanceof Error ? err.message : 'failed' });
    } finally {
      setResyncBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 text-sm">
        <div className="text-xs uppercase text-gray-500">Mail credit balance</div>
        <div className="text-lg tabular-nums">
          {customer.mail_credit_balance.toLocaleString()} sends
        </div>
        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
          Overage mode: <code className="font-mono">{customer.mail_overage_mode}</code> · Monthly cap:{' '}
          €{(customer.mail_monthly_overage_cap_eur_cents / 100).toFixed(2)}
        </div>
      </div>
      <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 text-sm">
        <div className="text-xs uppercase text-gray-500">Subscription usage</div>
        {mailUsage.source === 'unavailable' ? (
          <div className="mt-1 text-xs text-gray-500">
            orboto Mail isn't reachable from this OCP instance — usage data unavailable.
          </div>
        ) : mailUsage.sends_total === null ? (
          <div className="mt-1 text-xs text-gray-500">
            No subscription quota — customer is on Free or top-up only.
          </div>
        ) : (
          <div>
            <div className="text-lg tabular-nums">
              {(mailUsage.sends_used ?? 0).toLocaleString()} /{' '}
              {mailUsage.sends_total.toLocaleString()} sends
            </div>
            {mailUsage.period_end && (
              <div className="text-xs text-gray-500">
                Period ends {fmtDate(mailUsage.period_end)}
              </div>
            )}
          </div>
        )}
      </div>
      {/* OCP-250 — force-resync of OMS quota state. Use when a customer
          reports `quota_exhausted` despite an active sub on file (OMS
          drifted from OCP-authoritative state and the 5-min heartbeat
          hasn't recovered them yet). */}
      <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 text-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase text-gray-500">OMS quota sync</div>
            <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Push OCP-authoritative quota / tier / period to OMS now. Run
              when a customer reports `quota_exhausted` despite an active
              subscription on file.
            </div>
          </div>
          <button
            type="button"
            onClick={handleResync}
            disabled={resyncBusy}
            className="shrink-0 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
          >
            {resyncBusy ? 'Syncing…' : 'Resync OMS quota'}
          </button>
        </div>
        {resyncResult && (
          <div
            className={`mt-2 text-xs ${
              resyncResult.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'
            }`}
          >
            {resyncResult.ok ? '✓ Synced' : `Failed: ${resyncResult.reason}`}
          </div>
        )}
      </div>
    </div>
  );
}

function LicensesTab({ licenses }: { licenses: LicenseRow[] }) {
  if (licenses.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        No Self-Host licenses issued for this account.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm">
      <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
        <tr>
          <th className="px-3 py-2">Display name</th>
          <th className="px-3 py-2">Tier</th>
          <th className="px-3 py-2">Status</th>
          <th className="px-3 py-2 text-right">Instances</th>
          <th className="px-3 py-2">Key prefix</th>
          <th className="px-3 py-2">Issued</th>
          <th className="px-3 py-2">Expires</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
        {licenses.map((l) => (
          <tr key={l.id}>
            <td className="px-3 py-2">{l.display_name}</td>
            <td className="px-3 py-2 text-xs">{l.tier}</td>
            <td className="px-3 py-2 text-xs">{l.status}</td>
            <td className="px-3 py-2 text-right tabular-nums">
              {l.instance_count}
              {l.max_instances ? ` / ${l.max_instances}` : ''}
            </td>
            <td className="px-3 py-2 font-mono text-xs">{l.license_key_prefix}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
              {fmtDate(l.issued_at)}
            </td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
              {l.expires_at ? fmtDate(l.expires_at) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
      </table>
    </div>
  );
}

function ConfirmModal({
  action,
  customer,
  onCancel,
  onDone,
}: {
  action:
    | { kind: 'suspend' | 'unsuspend' | 'force-logout' }
    | { kind: 'reset-password'; userId: string; userEmail: string };
  customer: AccountDetail;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const challenge =
    action.kind === 'reset-password' ? action.userEmail : customer.email;
  const ready = typed.trim() === challenge;

  const config = (() => {
    switch (action.kind) {
      case 'suspend':
        return {
          title: 'Suspend account',
          body: `This revokes every active session and prevents the customer from logging back in until you unsuspend. Type the customer email "${challenge}" to confirm.`,
          cta: 'Suspend',
          danger: true,
          url: `/v1/admin/accounts/${customer.id}/suspend`,
        };
      case 'unsuspend':
        return {
          title: 'Unsuspend account',
          body: `This restores access. Type the customer email "${challenge}" to confirm.`,
          cta: 'Unsuspend',
          danger: false,
          url: `/v1/admin/accounts/${customer.id}/unsuspend`,
        };
      case 'force-logout':
        return {
          title: 'Force-logout all users',
          body: `This revokes every active session but leaves the account status unchanged. Type the customer email "${challenge}" to confirm.`,
          cta: 'Force-logout',
          danger: true,
          url: `/v1/admin/accounts/${customer.id}/force-logout`,
        };
      case 'reset-password':
        return {
          title: 'Send password-reset email',
          body: `This generates a reset token and emails ${action.userEmail} the reset link. Type "${challenge}" to confirm.`,
          cta: 'Send reset email',
          danger: false,
          url: `/v1/admin/accounts/${customer.id}/users/${action.userId}/trigger-password-reset`,
        };
    }
  })();

  async function submit() {
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api(config.url, { method: 'POST' });
      await onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`HTTP ${err.status}`);
      } else {
        setError('Action failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-3 text-base font-semibold">{config.title}</h2>
        <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">{config.body}</p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={challenge}
          className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 font-mono text-xs"
        />
        {error && (
          <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!ready || submitting}
            onClick={() => void submit()}
            className={`rounded px-3 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50 ${
              config.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-brand-500 hover:bg-brand-600'
            }`}
          >
            {submitting ? 'Submitting…' : config.cta}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: AccountStatus }) {
  const config = {
    active: { label: 'active', cls: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300' },
    suspended: { label: 'suspended', cls: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
    pending_email_verify: {
      label: 'pending verify',
      cls: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200',
    },
  }[status];
  return (
    <span className={`rounded border px-2 py-0.5 text-xs ${config.cls}`}>
      {config.label}
    </span>
  );
}

// ---- Instance card (OCP-474) ----------------------------------------
//
// Always-visible above the tabs because "is a workspace assigned?" is the
// headline question when an operator opens an account. Empty state is
// explicit; a provisioned tenant links out to its full detail page (which
// carries health + telemetry) so this card stays a cheap glance.

function InstanceCard({ tenants }: { tenants: TenantRow[] }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Instance{tenants.length > 1 ? `s (${tenants.length})` : ''}
        </h2>
      </div>
      {tenants.length === 0 ? (
        <div className="mt-3 rounded border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-4 text-sm text-gray-500">
          No workspace provisioned yet.
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {tenants.map((t) => (
            <TenantInstanceRow key={t.id} tenant={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function TenantInstanceRow({ tenant }: { tenant: TenantRow }) {
  const host = tenant.workspace_url.replace(/^https?:\/\//, '');
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to={`/tenants/${tenant.id}`}
          className="font-mono text-sm text-brand-500 hover:text-brand-600 hover:underline"
        >
          {tenant.subdomain}
        </Link>
        <TenantStatusPill status={tenant.status} />
        {tenant.is_demo && (
          <span className="rounded border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/30 px-2 py-0.5 text-xs text-sky-800 dark:text-sky-300">
            demo
          </span>
        )}
        <a
          href={tenant.workspace_url}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto text-xs text-brand-500 hover:text-brand-600 hover:underline"
        >
          {host} ↗
        </a>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
        <InstanceField label="Region" value={tenant.region} />
        <InstanceField label="Pool" value={tenant.pool_tag} />
        <InstanceField label="Worker" value={tenant.worker_name ?? 'unassigned'} />
        <InstanceField label="Storage" value={`${tenant.storage_quota_gb} GB`} />
        <InstanceField label="Version" value={tenant.image_tag ?? 'latest'} />
        <InstanceField label="Origin" value={tenant.origin} />
        <InstanceField
          label="License expires"
          value={
            tenant.license_expires_at
              ? fmtDate(tenant.license_expires_at)
              : '-'
          }
        />
        <InstanceField
          label="Provisioned"
          value={fmtDate(tenant.created_at)}
        />
        <InstanceField
          label="Activated"
          value={
            tenant.activated_at
              ? fmtDate(tenant.activated_at)
              : '-'
          }
        />
        {tenant.custom_domain && (
          <InstanceField
            label="Custom domain"
            value={tenant.custom_domain}
            hint={tenant.custom_domain_status ?? undefined}
          />
        )}
      </dl>
    </div>
  );
}

function InstanceField({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{value}</dd>
      {hint && <dd className="text-xs text-gray-500">{hint}</dd>}
    </div>
  );
}

// tenant_status enum (provisioning / active / past_due / paused / archived).
// Mirrors the AdminTenantsPage StatusBadge palette so the two operator
// surfaces read consistently.
function TenantStatusPill({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active:
      'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
    provisioning:
      'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    past_due:
      'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    paused:
      'border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
    archived:
      'border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400',
  };
  const cls =
    styles[status] ??
    'border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
  return <span className={`rounded border px-2 py-0.5 text-xs ${cls}`}>{status}</span>;
}

// ---- Pending-claim banner (OCP-515) ---------------------------------
//
// A non-null pending_redeem_code means this account signed up carrying a
// redeem code (from /redeem?code=X) but has not completed the claim yet -
// exactly the guido.goe symptom (signed up, then dropped into the account
// area instead of continuing to redeem). Rendered only when a carry is on
// file so a healthy account shows nothing; the operator sees at a glance who
// is stuck mid-claim and since when. Cleared automatically once the customer
// redeems any code.

function PendingClaimBanner({
  code,
  setAt,
}: {
  code: string | null;
  setAt: string | null;
}) {
  if (!code) return null;
  return (
    <div
      data-testid="pending-claim-banner"
      className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
          Claim in progress
        </h2>
        <span className="rounded bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 font-mono text-xs uppercase tracking-wider text-amber-800 dark:text-amber-300">
          {code}
        </span>
      </div>
      <p className="mt-2 text-sm text-amber-800 dark:text-amber-300">
        This account signed up with a redeem code in flight but has not
        completed the claim yet
        {setAt ? <> (carried since {fmtDateTime(setAt)})</> : null}.
        It clears automatically once the customer redeems any code.
      </p>
    </div>
  );
}

// ---- Responsible contact card (OCP-524) -----------------------------
//
// "Who is authorized to make and receive declarations for this account?" - the
// Terms-mandated contact person, plus the append-only text-form evidence trail
// of every change. Sits high on the page because it is the first thing an
// operator needs when a customer asserts something on behalf of the account.
//
// The card also carries the ONLY operator write path: recording a notice the
// customer sent out of band (mail / support ticket). That is not a convenience
// - a customer may change their contact by any notice in text form, and
// without this the change could not be honoured at all. The mandatory
// notice_reference is what keeps that path accountable.

function ResponsibleContactCard({
  customerId,
  contact,
  history,
  onRecorded,
}: {
  customerId: string;
  contact: ResponsibleContact | null;
  history: ResponsibleContactChange[] | undefined;
  onRecorded: () => Promise<void>;
}) {
  const [recording, setRecording] = useState(false);
  // Defensive: an API that predates this field (or a partial response) must
  // never blank the operator's whole account page over a missing array.
  const rows = history ?? [];

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Responsible contact
          </h2>
          {!contact && <MissingContactBadge />}
        </div>
        <button
          type="button"
          onClick={() => setRecording(true)}
          className="rounded bg-brand-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          data-testid="record-responsible-contact"
        >
          Record notice
        </button>
      </div>

      {contact ? (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
          <InstanceField label="Name" value={contact.name} />
          <InstanceField label="Email" value={contact.email} />
          <InstanceField label="Phone" value={contact.phone ?? '-'} />
          <InstanceField label="Position" value={contact.position ?? '-'} />
          <InstanceField
            label="Named on"
            value={fmtDateTime(contact.set_at)}
            hint="UTC"
          />
          <InstanceField
            label="Named by"
            value={contact.set_by}
            hint={contact.source === 'operator' ? 'operator-recorded notice' : 'portal'}
          />
        </dl>
      ) : (
        <div className="mt-3 rounded border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-4 text-sm text-gray-500">
          No responsible contact on file. This account predates the Terms clause (or has never
          concluded a contract) - it can use the portal normally, but any new contract
          conclusion is blocked until a contact is named.
        </div>
      )}

      <div className="mt-4">
        <h3 className="text-xs uppercase tracking-wide text-gray-500">
          Change history{rows.length > 0 ? ` (${rows.length})` : ''}
        </h3>
        {rows.length === 0 ? (
          <div className="mt-2 text-xs text-gray-500">No changes recorded.</div>
        ) : (
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">New contact</th>
                  <th className="px-3 py-2">Replaced</th>
                  <th className="px-3 py-2">By</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Text-form notice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map((h) => (
                  <tr key={h.id} data-testid={`contact-change-${h.id}`}>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                      {fmtDateTime(h.changed_at)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-gray-900 dark:text-gray-100">{h.new_name}</div>
                      <div className="font-mono text-xs text-gray-500">{h.new_email}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {h.previous_email ?? 'first naming'}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{h.changed_by}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className="rounded border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300">
                        {h.source}
                      </span>
                      {h.notice_reference && (
                        <div
                          className="mt-1 max-w-[220px] truncate text-[11px] text-gray-500"
                          title={h.notice_reference}
                        >
                          {h.notice_reference}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {h.notice_mail_id ? (
                        <span
                          className="font-mono text-emerald-700 dark:text-emerald-300"
                          title={(h.notice_recipients ?? [])
                            .map((r) => `${r.role}: ${r.email}`)
                            .join('\n')}
                        >
                          {h.notice_mail_id}
                        </span>
                      ) : (
                        <span
                          className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-amber-800 dark:text-amber-200"
                          title={h.notice_mail_error ?? 'no send id recorded'}
                        >
                          no evidence
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {recording && (
        <RecordResponsibleContactModal
          customerId={customerId}
          current={contact}
          onCancel={() => setRecording(false)}
          onDone={async () => {
            setRecording(false);
            await onRecorded();
          }}
        />
      )}
    </div>
  );
}

// ---- Orders card (OCP-526) ------------------------------------------
//
// "What exactly did this customer order and when" - answerable here instead of
// in the Merchant of Record's dashboard. Every value is the SNAPSHOT taken when
// the order was placed, so it still reads correctly after a repricing.
//
// The card also surfaces the confirmation gap: AGB V4 (OWS-D23) 1.1 makes
// the order confirmation one of the two ways the contract comes into effect,
// so a failed send is an evidence gap and gets a badge plus a resend button,
// not a swallowed error.

interface OrderRow {
  id: string;
  placed_at: string;
  placed_by_email: string;
  product_id: string;
  product_display_name: string;
  billing_period: string;
  billing_cycle_label: string;
  term_note: string;
  tax_note: string;
  order_button_label: string;
  seat_count: number;
  unit_amount_cents: number;
  total_amount_cents: number;
  /** OCP-991 - what a discount code took off the total; 0 without one. */
  discount_amount_cents?: number;
  /** OCP-1036 - the company package the order charged; null = list price. */
  company_package_id?: string | null;
  currency: string;
  terms_version: string | null;
  /** OCP-847 - the AVV accepted with the same click; null = none was in force (or order before OCP-847). */
  avv_version?: string | null;
  /** OCP-851 - the service price list (AGB 6.6) in force at the click; null = none published (or order before OCP-851). */
  service_price_version?: string | null;
  /** OCP-534 - the Sec. 14 BGB declaration as it stood when this order was placed. */
  business_capacity_statement: string | null;
  business_capacity_statement_version: string | null;
  business_capacity_confirmed_at: string | null;
  company_name: string | null;
  vat_id: string | null;
  provider: 'polar' | 'paddle' | 'none';
  provider_checkout_id: string | null;
  provider_order_id: string | null;
  subscription_id: string | null;
  status: 'placed' | 'confirmed' | 'failed';
  failure_reason: string | null;
  confirmed_at: string | null;
  confirmation_mail_id: string | null;
  confirmation_mail_error: string | null;
}

function formatOrderMoney(amountCents: number, currency: string): string {
  const abs = Math.abs(amountCents);
  return `${currency.toUpperCase()} ${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

function OrdersCard({
  customerId,
  orders,
  onChanged,
}: {
  customerId: string;
  orders: OrderRow[] | undefined;
  onChanged: () => Promise<void>;
}) {
  // Defensive: an API response that predates this field must never blank the
  // operator's whole account page over a missing array.
  const rows = orders ?? [];
  const [resending, setResending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const gaps = rows.filter((o) => o.confirmation_mail_error !== null).length;

  async function resend(orderId: string) {
    setResending(orderId);
    setError(null);
    try {
      await api(
        `/v1/admin/accounts/${customerId}/orders/${orderId}/resend-confirmation`,
        { method: 'POST' },
      );
      await onChanged();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `Resend failed (HTTP ${err.status}).`
          : 'Resend failed.',
      );
    } finally {
      setResending(null);
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Orders{rows.length > 0 ? ` (${rows.length})` : ''}
        </h2>
        {gaps > 0 && (
          <span
            title="Order confirmation could not be delivered - the contract record has an evidence gap"
            className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-200"
            data-testid="order-confirmation-gap-badge"
          >
            {gaps} unconfirmed by mail
          </span>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="mt-3 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="mt-3 rounded border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-4 text-sm text-gray-500">
          No orders recorded. Orders placed before OCP-526 shipped only exist in
          the Merchant of Record.
        </div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Placed</th>
                <th className="px-3 py-2">Ordered</th>
                <th className="px-3 py-2">Price</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Declaration</th>
                <th className="px-3 py-2">Confirmation</th>
                <th className="px-3 py-2">MoR</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.map((o) => (
                <tr key={o.id} data-testid={`order-${o.id}`}>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                    {fmtDateTime(o.placed_at)}
                    <div className="font-mono text-[11px] text-gray-400">
                      {o.placed_by_email}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-gray-900 dark:text-gray-100">
                      {o.product_display_name}
                    </div>
                    <div className="font-mono text-[11px] text-gray-500">{o.product_id}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="text-gray-900 dark:text-gray-100">
                      {formatOrderMoney(o.total_amount_cents, o.currency)}
                      {!!o.discount_amount_cents && (
                        <span className="ml-1 text-xs font-normal text-gray-500" data-testid="order-discount">
                          after {formatOrderMoney(o.discount_amount_cents, o.currency)} discount
                        </span>
                      )}
                      {o.company_package_id && (
                        <span className="ml-1 text-xs font-normal text-gray-500" data-testid="order-company-package">
                          company package
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {o.billing_cycle_label}
                      {o.seat_count > 1 ? ` · ${o.seat_count} seats` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className="rounded border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-gray-700 dark:text-gray-300">
                      {o.status}
                    </span>
                    {o.failure_reason && (
                      <div
                        className="mt-1 max-w-[200px] truncate text-[11px] text-gray-500"
                        title={o.failure_reason}
                      >
                        {o.failure_reason}
                      </div>
                    )}
                  </td>
                  {/* OCP-534 - the Sec. 14 BGB declaration frozen onto THIS
                      order. Hover shows the verbatim wording, so an operator
                      can answer "what exactly did they confirm" years later
                      even after the constant was reworded. */}
                  <td className="px-3 py-2 text-xs">
                    {o.business_capacity_confirmed_at ? (
                      <>
                        <span
                          className="rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 text-emerald-800 dark:text-emerald-200"
                          title={o.business_capacity_statement ?? ''}
                          data-testid={`order-business-capacity-${o.id}`}
                        >
                          {o.business_capacity_statement_version ?? 'confirmed'}
                        </span>
                        <div className="mt-1 text-[11px] text-gray-500">
                          {o.company_name ?? 'no company name'}
                          {o.vat_id ? ` · ${o.vat_id}` : ''}
                        </div>
                      </>
                    ) : (
                      <span
                        className="rounded border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-gray-600 dark:text-gray-400"
                        title="Order placed before the strictly-B2B decision (2026-07-26) - no declaration was captured"
                      >
                        pre-decision
                      </span>
                    )}
                    {/* OCP-847 - the AGB and AVV versions the order click accepted. */}
                    <div className="mt-1 text-[11px] text-gray-500" data-testid={`order-terms-${o.id}`}>
                      {o.terms_version
                        ? `AGB ${o.terms_version} · AVV ${o.avv_version ?? 'not published'}`
                        : 'AGB / AVV not recorded (order before OCP-847)'}
                    </div>
                    <div className="text-[11px] text-gray-500" data-testid={`order-service-prices-${o.id}`}>
                      {o.service_price_version
                        ? `Service price list ${o.service_price_version}`
                        : 'No service price list in force at the click'}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {o.confirmation_mail_id ? (
                      <span className="font-mono text-emerald-700 dark:text-emerald-300">
                        {o.confirmation_mail_id}
                      </span>
                    ) : (
                      <span
                        className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-amber-800 dark:text-amber-200"
                        title={o.confirmation_mail_error ?? 'not sent yet'}
                      >
                        {o.confirmation_mail_error ? 'send failed' : 'not sent'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="text-gray-500">{o.provider}</div>
                    {o.provider_order_id && (
                      <div
                        className="max-w-[180px] truncate font-mono text-[11px] text-gray-400"
                        title={o.provider_order_id}
                      >
                        {o.provider_order_id}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => void resend(o.id)}
                      disabled={resending !== null}
                      className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
                      data-testid={`resend-order-confirmation-${o.id}`}
                    >
                      {resending === o.id ? 'Sending…' : 'Resend confirmation'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Business capacity card (OCP-534) --------------------------------
//
// AGB V4 (OWS-D23) Präambel B: orboto contracts EXCLUSIVELY with entrepreneurs
// (Sec. 14 BGB), legal entities under public law and public-law special funds.
// The product enforces that with the customer's declaration and an operator
// refusal (the mechanism the superseded draft OWS-D6 § 1 (2) spelled out).
//
// This card is both halves on the operator side: what was declared (who, when,
// which wording) and the refuse switch. Refusing is deliberately NOT a suspend -
// running subscriptions keep running and the customer keeps full access to their
// data; only a NEW contract conclusion is blocked.

interface BusinessCapacityDeclarationRow {
  statement: string;
  statement_version: string;
  /** OCP-895 - de / en; null = before OCP-895 (English). */
  statement_language?: 'de' | 'en' | null;
  confirmed_at: string;
  confirmed_by: string;
}

interface BusinessCapacityAdminRow {
  statement_version_current: string;
  /** OCP-895 - the current tag per language. */
  statement_versions_current?: Record<'de' | 'en', string>;
  declaration: BusinessCapacityDeclarationRow | null;
  company_name: string | null;
  vat_id: string | null;
  can_conclude: boolean;
  blocked_reason:
    | 'business_capacity_missing'
    | 'company_name_missing'
    | 'business_capacity_refused'
    | null;
  refusal: {
    refused_at: string;
    reason: string;
    refused_by: string;
    note: string | null;
  } | null;
}

/** Shared "this account owes a declaration" pill (detail card + accounts list). */
export function MissingBusinessCapacityBadge() {
  return (
    <span
      title="No Sec. 14 BGB business-capacity declaration (or no company name) - new contract conclusions are blocked"
      className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-200"
      data-testid="missing-business-capacity-badge"
    >
      no declaration
    </span>
  );
}

/** Shared "operator refused this account" pill. */
export function ConsumerSuspectedBadge() {
  return (
    <span
      title="An operator has refused further contract conclusion on this account (Terms Sec. 1 (2))"
      className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-2 py-0.5 text-xs text-red-800 dark:text-red-200"
      data-testid="consumer-suspected-badge"
    >
      consumer suspected
    </span>
  );
}

function BusinessCapacityCard({
  customerId,
  capacity,
  onChanged,
}: {
  customerId: string;
  // Defensive: an API response that predates this field must never blank the
  // operator's whole account page.
  capacity: BusinessCapacityAdminRow | null | undefined;
  onChanged: () => Promise<void>;
}) {
  const [flagging, setFlagging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!capacity) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Business capacity
        </h2>
        <div className="mt-3 text-sm text-gray-500">Not available on this response.</div>
      </div>
    );
  }

  async function clearRefusal() {
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/admin/accounts/${customerId}/consumer-suspected`, {
        method: 'DELETE',
        body: {},
      });
      await onChanged();
    } catch (err) {
      // The admin ApiError carries only { status, body } - read the error key
      // through the exported helper, never `err.code`.
      const key = err instanceof ApiError ? readErrorKey(err.body) : null;
      setError(
        key ?? (err instanceof ApiError ? `Failed (HTTP ${err.status}).` : 'Failed.'),
      );
    } finally {
      setBusy(false);
    }
  }

  const refused = capacity.blocked_reason === 'business_capacity_refused';

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Business capacity
          </h2>
          {refused && <ConsumerSuspectedBadge />}
          {!refused && !capacity.can_conclude && <MissingBusinessCapacityBadge />}
        </div>
        {refused ? (
          <button
            type="button"
            onClick={() => void clearRefusal()}
            disabled={busy}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
            data-testid="clear-consumer-suspected"
          >
            {busy ? 'Clearing…' : 'Lift refusal'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setFlagging(true)}
            className="rounded border border-red-300 dark:border-red-800 bg-white dark:bg-gray-900 px-2.5 py-1 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
            data-testid="flag-consumer-suspected"
          >
            Refuse new orders
          </button>
        )}
      </div>

      {error && (
        <div className="mt-3 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {refused && capacity.refusal && (
        <div
          className="mt-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-3 text-sm text-red-900 dark:text-red-200"
          data-testid="consumer-suspected-detail"
        >
          <div className="text-xs uppercase tracking-wide text-red-700 dark:text-red-300">
            Shown to the customer
          </div>
          <div className="mt-1">{capacity.refusal.reason}</div>
          {capacity.refusal.note && (
            <>
              <div className="mt-2 text-xs uppercase tracking-wide text-red-700 dark:text-red-300">
                Operator note (never shown to the customer)
              </div>
              <div className="mt-1 text-xs">{capacity.refusal.note}</div>
            </>
          )}
          <div className="mt-2 text-[11px] text-red-700 dark:text-red-300">
            Refused {fmtDateTime(capacity.refusal.refused_at)}
            by {capacity.refusal.refused_by}. Existing subscriptions are unaffected.
          </div>
        </div>
      )}

      {capacity.declaration ? (
        <>
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
            <InstanceField label="Company" value={capacity.company_name ?? '-'} />
            <InstanceField
              label="VAT ID"
              value={capacity.vat_id ?? '-'}
              hint="optional, never a gate"
            />
            <InstanceField
              label="Confirmed on"
              value={fmtDateTime(capacity.declaration.confirmed_at)}
              hint="Europe/Berlin"
            />
            <InstanceField
              label="Confirmed by"
              value={capacity.declaration.confirmed_by}
              hint={
                // OCP-895 - compared with the current tag of the declaration's OWN language
                (capacity.statement_versions_current?.[capacity.declaration.statement_language ?? 'en'] ??
                  capacity.statement_version_current) === capacity.declaration.statement_version
                  ? `${capacity.declaration.statement_version}${capacity.declaration.statement_language ? ` (${capacity.declaration.statement_language})` : ''}`
                  : `${capacity.declaration.statement_version} (superseded)`
              }
            />
          </dl>
          <blockquote
            className="mt-3 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3 text-xs text-gray-600 dark:text-gray-400"
            data-testid="business-capacity-statement"
          >
            {capacity.declaration.statement}
          </blockquote>
        </>
      ) : (
        <div className="mt-3 rounded border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-4 text-sm text-gray-500">
          No business-capacity declaration on file. This account predates the
          strictly-B2B decision (2026-07-26) or has never concluded a contract -
          it can use the portal normally, but any new contract conclusion is
          blocked until the declaration and a company name are on file. The
          portal prompts the customer with a banner.
        </div>
      )}

      {capacity.blocked_reason === 'company_name_missing' && (
        <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
          The declaration is on file but the company name is missing, so a new
          contract conclusion is still blocked.
        </p>
      )}

      {flagging && (
        <FlagConsumerSuspectedModal
          customerId={customerId}
          onCancel={() => setFlagging(false)}
          onDone={async () => {
            setFlagging(false);
            await onChanged();
          }}
        />
      )}
    </div>
  );
}

/**
 * The refusal dialog. `reason` is CUSTOMER-FACING - it is rendered verbatim in
 * the portal when the customer's next order is blocked - so it is mandatory and
 * the label says so. `note` stays on the operator surface.
 */
function FlagConsumerSuspectedModal({
  customerId,
  onCancel,
  onDone,
}: {
  customerId: string;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState(
    'We could not confirm that this account is ordering as a business. Please contact us so we can verify your business status.',
  );
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = reason.trim().length > 0;

  async function submit() {
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      await api(`/v1/admin/accounts/${customerId}/consumer-suspected`, {
        method: 'POST',
        body: { reason: reason.trim(), note: note.trim() || null },
      });
      await onDone();
    } catch (err) {
      const key = err instanceof ApiError ? readErrorKey(err.body) : null;
      if (key === 'reason_required') {
        setError('A customer-facing reason is required.');
      } else if (err instanceof ApiError) {
        setError(`Failed (HTTP ${err.status}).`);
      } else {
        setError('Failed.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Refuse new orders"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-xl"
      >
        <h2 className="text-base font-semibold">Refuse new orders on this account</h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          AGB V4 Präambel B lets orboto verify business status and refuse an order
          placed by a consumer. This blocks checkout, free activation and code
          redemption. Existing subscriptions keep running and the customer keeps
          full access to their data.
        </p>

        {error && (
          <div
            role="alert"
            className="mt-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <label className="mt-4 block text-sm">
          <span className="text-gray-700 dark:text-gray-300">
            Reason shown to the customer
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
            data-testid="consumer-suspected-reason"
          />
          <span className="mt-1 block text-xs text-gray-500">
            Rendered verbatim in the portal. Required.
          </span>
        </label>

        <label className="mt-3 block text-sm">
          <span className="text-gray-700 dark:text-gray-300">
            Operator note <span className="text-gray-500">(optional, internal)</span>
          </span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="What made us suspect a consumer?"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
            data-testid="consumer-suspected-note"
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!valid || saving}
            className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:bg-gray-300"
            data-testid="consumer-suspected-save"
          >
            {saving ? 'Saving…' : 'Refuse new orders'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Shared "this account owes a contact" pill (detail card + accounts list). */
export function MissingContactBadge() {
  return (
    <span
      title="No authorized responsible contact person named - new contract conclusions are blocked"
      className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-200"
      data-testid="missing-responsible-contact-badge"
    >
      no contact
    </span>
  );
}

function RecordResponsibleContactModal({
  customerId,
  current,
  onCancel,
  onDone,
}: {
  customerId: string;
  current: ResponsibleContact | null;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [name, setName] = useState(current?.name ?? '');
  const [email, setEmail] = useState(current?.email ?? '');
  const [phone, setPhone] = useState(current?.phone ?? '');
  const [position, setPosition] = useState(current?.position ?? '');
  const [noticeReference, setNoticeReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (submitting) return;
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required.');
      return;
    }
    if (!noticeReference.trim()) {
      setError('A notice reference is required - point at the customer declaration.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api(`/v1/admin/accounts/${customerId}/responsible-contact`, {
        method: 'POST',
        body: {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          position: position.trim() || null,
          notice_reference: noticeReference.trim(),
        },
      });
      await onDone();
    } catch (err) {
      // The admin ApiError carries only { status, body } - read the error key
      // through the exported helper, never `err.code`.
      const key = err instanceof ApiError ? readErrorKey(err.body) : null;
      if (key === 'notice_reference_required') {
        setError('A notice reference is required.');
      } else if (key === 'invalid_contact') {
        setError('That contact is not valid - check the name and email.');
      } else if (err instanceof ApiError) {
        setError(`Failed (HTTP ${err.status}).`);
      } else {
        setError('Failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Record responsible-contact notice"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(ev) => ev.stopPropagation()}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-1 text-base font-semibold">Record responsible-contact notice</h2>
        <p className="mb-3 text-xs text-gray-500">
          Use this only to transcribe a notice the CUSTOMER sent in text form (email, support
          ticket). orboto mails the confirmation to the new contact, the replaced contact and
          the account address, and the change is written to the append-only history.
        </p>
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-gray-500">Name</span>
            <input
              type="text"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              maxLength={200}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
              data-testid="record-contact-name"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-gray-500">Email</span>
            <input
              type="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              maxLength={254}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
              data-testid="record-contact-email"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-sm">
              <span className="text-xs uppercase tracking-wide text-gray-500">Phone</span>
              <input
                type="tel"
                value={phone}
                onChange={(ev) => setPhone(ev.target.value)}
                maxLength={64}
                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs uppercase tracking-wide text-gray-500">Position</span>
              <input
                type="text"
                value={position}
                onChange={(ev) => setPosition(ev.target.value)}
                maxLength={200}
                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-gray-500">
              Notice reference (required)
            </span>
            <textarea
              value={noticeReference}
              onChange={(ev) => setNoticeReference(ev.target.value)}
              rows={2}
              maxLength={500}
              placeholder='e.g. Mail 2026-07-25 "Neuer Ansprechpartner" / OWS-42'
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
              data-testid="record-contact-reference"
            />
            <span className="mt-1 block text-[11px] text-gray-500">
              Where the customer declaration lives. Stored on the history row forever.
            </span>
          </label>
        </div>
        {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => void submit()}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            data-testid="record-contact-submit"
          >
            {submitting ? 'Saving…' : 'Record notice'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Origin card (OCP-475) ------------------------------------------
//
// "Where did this customer come from?" - the invite/prepaid code they
// redeemed and, when that code was minted off the waitlist, the linked
// waitlist entry. Sits next to the Instance card so an operator answering
// an acquisition question sees the workspace AND its origin in one glance.
// Explicit empty state for manually-created / demo accounts (no code).

function OriginCard({ origin }: { origin: AccountOrigin }) {
  const codes = origin?.codes ?? [];
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Origin{codes.length > 1 ? ` (${codes.length})` : ''}
        </h2>
        {codes.length > 0 && (
          <Link to="/codes" className="text-xs text-brand-500 hover:text-brand-600 hover:underline">
            All codes ↗
          </Link>
        )}
      </div>
      {codes.length === 0 ? (
        <div className="mt-3 rounded border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-4 text-sm text-gray-500">
          No invite code or waitlist origin on file - manually created or demo account.
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {codes.map((c) => (
            <OriginCodeRow key={c.code_id} code={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function OriginTag({ children }: { children: ReactNode }) {
  return (
    <span className="rounded border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300">
      {children}
    </span>
  );
}

function OriginCodeRow({ code }: { code: OriginCode }) {
  const on = code.redeemed_at ? fmtDate(code.redeemed_at) : null;
  const verb = code.source === 'ledger' ? 'Redeemed' : 'Provisioned from';
  const dateLabel = code.source === 'ledger' ? 'redeemed' : 'provisioned';
  const duration = code.duration_months == null ? 'lifetime' : `${code.duration_months} mo`;
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-700 dark:text-gray-300">{verb} code</span>
        <Link
          to="/codes"
          className="font-mono text-sm text-brand-500 hover:text-brand-600 hover:underline"
        >
          {code.code}
        </Link>
        <OriginTag>{code.tier}</OriginTag>
        <OriginTag>{code.category}</OriginTag>
        {code.source === 'tenant_origin' && (
          <span
            title="Derived from the tenant origin - no redemption-ledger row on file"
            className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-200"
          >
            inferred
          </span>
        )}
        {on && (
          <span className="ml-auto text-xs text-gray-500">
            {dateLabel} {on}
          </span>
        )}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
        <InstanceField label="Kind" value={code.kind} />
        <InstanceField label="Duration" value={duration} />
        {code.seats != null && <InstanceField label="Seats" value={String(code.seats)} />}
        <InstanceField label="Issued by" value={code.created_by} />
        {code.redeemer_email && <InstanceField label="Redeemer" value={code.redeemer_email} />}
      </dl>
      {code.note && (
        <div className="mt-2 text-xs text-gray-500">Note: {code.note}</div>
      )}
      {code.waitlist && <OriginWaitlistBlock waitlist={code.waitlist} />}
    </div>
  );
}

function OriginWaitlistBlock({ waitlist }: { waitlist: OriginWaitlist }) {
  const joined = fmtDate(waitlist.created_at);
  const signedUp = waitlist.signed_up_at
    ? fmtDate(waitlist.signed_up_at)
    : null;
  const invited = waitlist.invited_at
    ? fmtDate(waitlist.invited_at)
    : null;
  const utm = [waitlist.utm_source, waitlist.utm_medium, waitlist.utm_campaign]
    .filter(Boolean)
    .join(' / ');
  return (
    <div className="mt-3 rounded border border-sky-200 dark:border-sky-800 bg-sky-50/60 dark:bg-sky-900/20 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/30 px-2 py-0.5 text-xs text-sky-800 dark:text-sky-300">
          waitlist conversion
        </span>
        <span className="font-mono text-xs text-gray-600 dark:text-gray-400">{waitlist.email}</span>
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
        <InstanceField label="Waitlist status" value={waitlist.status} />
        {waitlist.company && <InstanceField label="Company" value={waitlist.company} />}
        {waitlist.team_size_bucket && (
          <InstanceField label="Team size" value={waitlist.team_size_bucket} />
        )}
        <InstanceField label="Joined waitlist" value={joined} />
        <InstanceField label="Invited" value={invited ?? '-'} />
        <InstanceField label="Signed up" value={signedUp ?? '-'} />
        {utm && <InstanceField label="Signup UTM" value={utm} />}
      </dl>
    </div>
  );
}

// ---- Wallet tab (OCP-320) -------------------------------------------

interface WalletLedgerEntry {
  id: string;
  direction: 'credit' | 'debit';
  amount_cents: number;
  amount_microcents: number;
  balance_after_cents: number;
  balance_after_microcents: number;
  product: string;
  source: string;
  source_ref: string | null;
  note: string | null;
  created_at: string;
}

// Wallet amounts are microcents (1 cent = 10,000) since OCP-372. Show
// sub-cent precision (up to 4 decimals) when an amount isn't a whole
// cent so a per-send Mail debit renders as e.g. -€0.0005, not €0.00.
function formatMicrocentsEur(microcents: number): string {
  const euros = microcents / 1_000_000;
  const wholeEuro = microcents % 1_000_000 === 0;
  const subCent = microcents % 10_000 !== 0;
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: wholeEuro ? 0 : 2,
      maximumFractionDigits: subCent ? 4 : 2,
    }).format(euros);
  } catch {
    return `${euros.toFixed(subCent ? 4 : 2)} EUR`;
  }
}

function WalletTab({
  customerId,
  initialBalanceMicrocents,
}: {
  customerId: string;
  initialBalanceMicrocents: number;
}) {
  const [balance, setBalance] = useState<number>(initialBalanceMicrocents);
  // OCP-786 - refunded purchases the customer had already spent.
  const [refundDebts, setRefundDebts] = useState<Array<{ provider: string; source_ref: string; outstanding_microcents: number }>>([]);
  const [entries, setEntries] = useState<WalletLedgerEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [adjusting, setAdjusting] = useState(false);

  const load = useCallback(
    async (cursor: string | null) => {
      const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
      const r = await api<{
        balance_cents: number;
        balance_microcents: number;
        refund_debts?: Array<{ provider: string; source_ref: string; outstanding_microcents: number }>;
        entries: WalletLedgerEntry[];
        next_cursor: string | null;
      }>(`/v1/admin/accounts/${customerId}/wallet/ledger${qs}`);
      setBalance(r.balance_microcents);
      if (!cursor) setRefundDebts(r.refund_debts ?? []);
      setEntries((prev) => (cursor ? [...prev, ...r.entries] : r.entries));
      setNextCursor(r.next_cursor);
    },
    [customerId],
  );

  useEffect(() => {
    setError(null);
    void load(null).catch((err) =>
      setError(err instanceof ApiError ? `Load failed (HTTP ${err.status}).` : 'Load failed.'),
    );
  }, [load]);

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500">Wallet balance</p>
          <p className="mt-1 text-2xl font-semibold">{formatMicrocentsEur(balance)}</p>
          <p className="mt-0.5 text-xs text-gray-500" data-testid="wallet-balance-tokens" title="OCP-660: 1 orboto Token = 1 cent = 10,000 microcents; the customer sees these as tokens (always on since OCP-668)">
            = {Math.round(balance / 10_000).toLocaleString('en-GB')} orboto Token
          </p>
          {refundDebts.length > 0 && (
            <p className="mt-1 text-xs text-red-700 dark:text-red-300" data-testid="wallet-refund-debt">
              Refund debt {formatMicrocentsEur(refundDebts.reduce((s, d) => s + d.outstanding_microcents, 0))} (
              {refundDebts.map((d) => `${d.provider} ${d.source_ref}`).join(', ')}): refunded purchase already spent;
              settled from the next credit before it is spendable.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setAdjusting(true)}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          Adjust balance
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="rounded border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
          No wallet activity.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-right" title="orboto Token (1 token = 1 cent)">Token</th>
                <th className="px-3 py-2 text-right">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                    {fmtDateTime(e.created_at)}
                  </td>
                  <td className="px-3 py-2 text-xs">{e.product}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{e.source}</td>
                  <td className={`px-3 py-2 text-right text-xs font-medium ${e.direction === 'credit' ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-gray-100'}`}>
                    {e.direction === 'credit' ? '+' : '−'}
                    {formatMicrocentsEur(e.amount_microcents)}
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-gray-500">{`${e.direction === 'credit' ? '+' : '-'}${Math.round(e.amount_microcents / 10_000).toLocaleString('en-GB')}`}</td>
                  <td className="px-3 py-2 text-right text-xs text-gray-500">{formatMicrocentsEur(e.balance_after_microcents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nextCursor && (
        <div className="text-center">
          <button
            type="button"
            disabled={loadingMore}
            onClick={() => {
              setLoadingMore(true);
              void load(nextCursor)
                .catch(() => setError('Could not load more.'))
                .finally(() => setLoadingMore(false));
            }}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {adjusting && (
        <AdjustWalletModal
          customerId={customerId}
          onCancel={() => setAdjusting(false)}
          onDone={async () => {
            setAdjusting(false);
            await load(null);
          }}
        />
      )}
    </div>
  );
}

function AdjustWalletModal({
  customerId,
  onCancel,
  onDone,
}: {
  customerId: string;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [direction, setDirection] = useState<'credit' | 'debit'>('credit');
  const [amountEur, setAmountEur] = useState('');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // OCP-930 - money moves on one rail for every caller: preview -> single-use
  // confirmation token -> execute. The token binds every field below, so any
  // edit after the preview discards it.
  const [preview, setPreview] = useState<{ balance_before_cents: number; balance_after_cents: number; sufficient: boolean; confirmation_token: string } | null>(null);

  function body() {
    const cents = Math.round(Number(amountEur) * 100);
    return {
      cents,
      payload: { direction, amount_cents: cents, reason: reason.trim(), ...(direction === 'credit' && expiresAt ? { expires_at: new Date(`${expiresAt}T23:59:59Z`).toISOString() } : {}) },
    };
  }

  function edited<T>(set: (v: T) => void): (v: T) => void {
    return (v: T) => {
      set(v);
      setPreview(null);
    };
  }

  async function runPreview() {
    if (submitting) return;
    const { cents, payload } = body();
    if (!Number.isInteger(cents) || cents <= 0) {
      setError('Enter a positive amount.');
      return;
    }
    if (!reason.trim()) {
      setError('A reason is required (audit log).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      setPreview(await api(`/v1/admin/accounts/${customerId}/wallet/adjust/preview`, { method: 'POST', body: payload }));
    } catch (err) {
      setError(err instanceof ApiError ? `Preview failed (HTTP ${err.status}).` : 'Preview failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function confirm() {
    if (submitting || !preview) return;
    setSubmitting(true);
    setError(null);
    try {
      await api(`/v1/admin/accounts/${customerId}/wallet/adjust`, {
        method: 'POST',
        body: { ...body().payload, confirmation_token: preview.confirmation_token },
      });
      await onDone();
    } catch (err) {
      const code = err instanceof ApiError ? (err.body as { error?: string } | null)?.error : undefined;
      if (err instanceof ApiError && err.status === 402) setError('Insufficient balance for that debit. Preview again to retry.');
      else if (code === 'confirmation_used' || code === 'confirmation_expired' || code === 'confirmation_mismatch') setError('The preview is no longer valid - preview again.');
      else setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Failed.');
      setPreview(null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Adjust wallet balance"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(ev) => ev.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-3 text-base font-semibold">Adjust wallet balance</h2>
        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => edited(setDirection)('credit')}
              className={`flex-1 rounded border px-3 py-1.5 text-sm ${direction === 'credit' ? 'border-brand-500 text-brand-700' : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400'}`}
            >
              Credit (+)
            </button>
            <button
              type="button"
              onClick={() => edited(setDirection)('debit')}
              className={`flex-1 rounded border px-3 py-1.5 text-sm ${direction === 'debit' ? 'border-red-500 text-red-600' : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400'}`}
            >
              Debit (−)
            </button>
          </div>
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-gray-500">Amount (EUR)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amountEur}
              onChange={(ev) => edited(setAmountEur)(ev.target.value)}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
            />
          </label>
          {direction === 'credit' && (
            <label className="block text-sm">
              <span className="text-xs uppercase tracking-wide text-gray-500">Expires on (optional, OCP-660)</span>
              <input
                type="date"
                value={expiresAt}
                onChange={(ev) => edited(setExpiresAt)(ev.target.value)}
                data-testid="wallet-adjust-expires"
                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1 text-sm"
              />
              <span className="mt-1 block text-xs text-gray-500">A credit with a date lapses unused like a purchased pack (AGB V4 3.2); without a date it never expires.</span>
            </label>
          )}
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-gray-500">Reason (audit)</span>
            <textarea
              value={reason}
              onChange={(ev) => edited(setReason)(ev.target.value)}
              rows={2}
              placeholder="e.g. goodwill credit / refund / correction"
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
            />
          </label>
        </div>
        {preview && (
          <div data-testid="wallet-adjust-preview" className="mt-3 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
            Balance {(preview.balance_before_cents / 100).toFixed(2)} EUR {'->'} {(preview.balance_after_cents / 100).toFixed(2)} EUR
            {!preview.sufficient && <span className="ml-1 text-red-700 dark:text-red-300">(not enough balance - the debit will be refused)</span>}
            <span className="mt-1 block text-gray-500">The confirmation is valid once, for 10 minutes, for exactly these values.</span>
          </div>
        )}
        {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          {preview ? (
            <button
              type="button"
              disabled={submitting}
              onClick={() => void confirm()}
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Confirm'}
            </button>
          ) : (
            <button
              type="button"
              disabled={submitting}
              onClick={() => void runPreview()}
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {submitting ? 'Checking…' : 'Preview'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
